import { useEffect } from 'react';

import { getAuthMode, getLocationHash } from 'js/lib/authModeDeepLinkUtils';
import type { InjectedRouter } from 'js/lib/connectToRouter';
import cookie from 'js/lib/cookie';
import redirect from 'js/lib/coursera.redirect';

import * as JWTLoginAPIUtils from 'bundles/saml-sso/utils/JWTLoginAPIUtils';
import {
  EXPIRED_INVITE_LINK_ERROR_CODE,
  INVALID_INVITE_LINK_ERROR_CODE,
} from 'bundles/saml-sso/utils/JWTLoginAPIUtils';

type JwtInviteCheckProps = {
  router: InjectedRouter;
  isProgramAdmin?: boolean;
  isAuthenticatedUser: boolean;
  programMembership: { isInvited?: boolean; isEmailNotVerified: boolean };
};

/**
 * Trade-off: the expired path deliberately KEEPS `organization`/`webToken` in the URL (instead of
 * stripping them into component state) so the resend dialog can derive its identifiers from the
 * router alone and survive a reload. This means a signed `webToken` persists in the address bar,
 * browser history, and outbound `Referer` headers for as long as the errorCode is present. The
 * plain-invalid path below still nulls both out, since it never offers a resend.
 */
async function redirectWhenTokenIsInvalid(
  response: JWTLoginAPIUtils.TokenValidationFailure | undefined,
  router: InjectedRouter
): Promise<void> {
  const invalidWebTokenArguments = {
    pathname: router.location.pathname,
    params: router.params,
    query: {
      ...router.location.query,
      organization: null,
      webToken: null,
      errorCode: INVALID_INVITE_LINK_ERROR_CODE,
    },
  };
  const expiredSigningKeyArguments = {
    pathname: router.location.pathname,
    params: router.params,
    query: {
      ...router.location.query,
      errorCode: EXPIRED_INVITE_LINK_ERROR_CODE,
    },
  };

  if (response?.errorCode === 'expired') {
    router.replace(expiredSigningKeyArguments);
  } else {
    router.replace(invalidWebTokenArguments);
  }
}

const useJwtInviteCheck = ({
  router,
  programMembership,
  isProgramAdmin,
  isAuthenticatedUser,
}: JwtInviteCheckProps): void => {
  useEffect(() => {
    const getTokenAndRedirect = async () => {
      try {
        const response = await JWTLoginAPIUtils.exchangeJwtInviteToken(router);
        if (!response) return;

        if ('id' in response) {
          const returnUrlBuilder = new URL(window.location.href);
          returnUrlBuilder.searchParams.delete('organization');
          returnUrlBuilder.searchParams.delete('webToken');
          const returnUrl = returnUrlBuilder.toString();
          cookie.set('returnTo', returnUrl, { expires: 1 });
          redirect.setLocation(`/sso/return/jwt_invite/${response.id}?returnTo=${encodeURIComponent(returnUrl)}`);
        } else {
          redirectWhenTokenIsInvalid(response, router);
        }
      } catch (e) {
        redirectWhenTokenIsInvalid(e.responseJSON as JWTLoginAPIUtils.TokenValidationFailure, router);
      }
    };

    const { organization, webToken, invitationToken, errorCode } = router.location.query;
    const authMode = getAuthMode(router.location.query as Record<string, unknown>, getLocationHash(router.location));
    const shouldTryJwtRedirect =
      // query params needed for jwt validation
      organization &&
      (webToken || invitationToken) &&
      // bypass links we have already tried and failed to exchange, so a remount does not re-run the
      // exchange in a loop
      errorCode !== EXPIRED_INVITE_LINK_ERROR_CODE &&
      // bypass for login form (has its own version of this)
      !authMode &&
      // bypass for learners not invited to the program, program membership will always be defined
      (!isAuthenticatedUser || programMembership.isInvited || programMembership.isEmailNotVerified) &&
      // bypass for admins
      !isProgramAdmin;

    if (!shouldTryJwtRedirect) return;

    getTokenAndRedirect();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useJwtInviteCheck;
